import { useEffect, useState } from "react";

import { Button, Classes } from "@blueprintjs/core";
import { toast } from "sonner";

import { Box, BoxProps } from "@/components/layout/flexbox";
import { SimpleDialog } from "@/components/pieces/simple-dialog";
import ClientCommentBlock from "@/components/pieces/talent-agents/ClientCommentBlock";
import {
  useCoachRatingsLazyQuery,
  useUpdateRatingFeaturedMutation,
} from "@/graphql";
import { CoachRating, ListAgentProfile } from "@/types/coaches";
import { ensureNonNullArray } from "@/utils/array";

const NUM_RATINGS_PER_PAGE = 5;

interface ClientCommentsContainerProps {
  profile: ListAgentProfile;
  enableRatingMenu?: boolean;
  onSuccess: () => void;
  onError: () => void;
}

function ClientCommentsContainer({
  profile,
  enableRatingMenu = false,
  onSuccess,
  onError,
}: ClientCommentsContainerProps) {
  const [firstLoad, setFirstLoad] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [ratings, setRatings] = useState<CoachRating[]>([]);
  const [overLimitError, setOverLimitError] = useState(false);

  const [fetch, { loading: fetchLoading }] = useCoachRatingsLazyQuery({
    fetchPolicy: "cache-and-network",
    onCompleted: (payload) => {
      if (!payload || !payload.coachRatings || !payload.coachRatings.length) {
        setHasMore(false);
      }

      const coachRatings = ensureNonNullArray(payload?.coachRatings);

      if (coachRatings.length < NUM_RATINGS_PER_PAGE) {
        setHasMore(false);
      }

      if (!firstLoad) {
        setFirstLoad(true);
      }

      setOffset(offset + coachRatings.length);
      setRatings((oldComment) => [...oldComment, ...coachRatings]);
    },
  });

  const [updateRatingFeatured] = useUpdateRatingFeaturedMutation({
    onCompleted: (data) => {
      const { ok } = data?.updateRatingFeatured || {};
      if (ok) {
        onSuccess();

        setOffset(0);
        setRatings([]);
        setFirstLoad(false);
        setHasMore(true);
        loadNextPage(0);
      }
    },
    onError: (error) => {
      if (
        error &&
        error.message &&
        error.message.includes("Over Featured Rating Limit")
      ) {
        setOverLimitError(true);
      } else {
        onError();
      }
    },
  });

  function loadNextPage(specificOffset = 0) {
    fetch({
      variables: {
        coachId: profile?.user?.id || "",
        limit: NUM_RATINGS_PER_PAGE,
        offset: specificOffset,
      },
    });
  }

  function updateRatingFeaturedStatus(id: string, isFeatured: boolean) {
    updateRatingFeatured({
      variables: {
        id,
        isFeatured,
      },
    });
  }

  useEffect(() => {
    loadNextPage();
  }, []);

  if (!ratings.length) return null;

  return (
    <>
      <h4 className="text-2xl font-serif">Recent Client Comments</h4>
      <Box my={4}>
        {!firstLoad && fetchLoading ? (
          <Box mt={24} height={300} className={Classes.SKELETON} />
        ) : null}
        {ratings.map((rating, i) => (
          <ClientCommentBlock
            key={`rating-${rating.id}`}
            rating={rating}
            onPressFeatureRating={updateRatingFeaturedStatus}
            enableRatingMenu={enableRatingMenu}
            mt={i > 0 ? 24 : 0}
          />
        ))}
        {hasMore && firstLoad ? (
          <Box mt={3}>
            <Button loading={fetchLoading} onClick={() => loadNextPage(offset)}>
              Load More
            </Button>
          </Box>
        ) : null}
        <SimpleDialog
          onClose={() => setOverLimitError(false)}
          isOpen={overLimitError}
          title="Unable to feature comment"
        >
          You can feature up to 5 comments on your profile. You are currently at
          the limit. Please first stop featuring one of the current featured
          comments in order to feature a new one.
        </SimpleDialog>
      </Box>
    </>
  );
}

interface ClientCommentsProps extends BoxProps {
  profile: ListAgentProfile;
  enableRatingMenu?: boolean;
  className?: string;
}

export function ClientComments({
  profile,
  enableRatingMenu,
}: ClientCommentsProps) {
  const showSuccessToast = () => {
    toast.success("Update featured rating success");
  };

  const showErrorToast = () => {
    toast.error("Failed to update featured rating");
  };

  return (
    <Box>
      <ClientCommentsContainer
        profile={profile}
        enableRatingMenu={enableRatingMenu}
        onSuccess={showSuccessToast}
        onError={showErrorToast}
      />
    </Box>
  );
}
